import React from 'react';
import SafeIcon from '../common/SafeIcon';

const steps = [
  {
    num: '01',
    title: 'Choose Your Spot',
    desc: 'Select either a Standard Ad or Featured Ad placement.',
  },
  {
    num: '02',
    title: 'We Design Your Ad',
    desc: 'Send us your logo, photos, and contact information. We create the ad for you at no additional charge.',
  },
  {
    num: '03',
    title: 'We Mail It',
    desc: 'Your ad is delivered directly to thousands of local households through our monthly neighborhood mailer.',
  },
];

const standardFeatures = [
  'Business card sized advertisement',
  'Full color printing',
  'Professional ad design included',
  'Placement on our oversized 9x12 neighborhood mailer',
  'QR code included if desired',
  'Direct exposure to local homeowners',
];

const featuredFeatures = [
  'Double-sized advertisement',
  'Premium visibility',
  'Full color printing',
  'Professional ad design included',
  'QR code included',
  'Increased space for photos and offers',
  'Ideal for businesses wanting maximum exposure',
];

const discounts = [
  { label: '1 Mailing', savings: 'Regular Price', highlight: false },
  { label: '3 Mailings', savings: 'Save 10%', highlight: false },
  { label: '6 Mailings', savings: 'Save 20%', highlight: true },
];

const Step = ({ num, title, desc }) => (
  <div className="flex gap-6 items-start">
    <div className="shrink-0 w-16 h-16 bg-retro-yellow border-4 border-retro-brown rounded-2xl flex items-center justify-center font-display text-3xl shadow-retro-sm">
      {num}
    </div>
    <div>
      <h3 className="font-display text-2xl uppercase tracking-tight mb-2">{title}</h3>
      <p className="font-body font-bold text-gray-600 leading-snug">{desc}</p>
    </div>
  </div>
);

const PriceCard = ({ title, price, features, isPopular }) => (
  <div className={`relative border-4 border-retro-brown rounded-[2.5rem] p-8 flex flex-col bg-white ${isPopular ? 'shadow-[12px_12px_0px_0px_rgba(58,44,39,1)] lg:-translate-y-4' : 'shadow-retro'}`}>
    {isPopular && (
      <div className="absolute top-4 right-4 bg-retro-yellow border-2 border-retro-brown px-3 py-1 rounded-full font-display text-[10px] uppercase tracking-widest transform rotate-3">
        Best Value
      </div>
    )}
    <div className="mb-8">
      <h3 className="font-display text-3xl uppercase mb-1">{title}</h3>
      <div className="flex items-baseline gap-1 mt-3">
        <span className="font-display text-6xl">${price}</span>
        <span className="font-body font-bold text-gray-400">/ mailing</span>
      </div>
    </div>
    <ul className="space-y-4 flex-1">
      {features.map((f, i) => (
        <li key={i} className="flex gap-3 font-body font-bold text-sm">
          <SafeIcon name="Check" className="text-retro-teal shrink-0 mt-0.5" />
          <span>{f}</span>
        </li>
      ))}
    </ul>
  </div>
);

const HowItWorks = () => (
  <div className="min-h-screen">

    {/* Hero banner */}
    <div className="border-b-4 border-retro-brown bg-retro-teal py-16 px-6 text-center">
      <h1 className="font-display text-5xl md:text-7xl uppercase tracking-tighter text-retro-cream drop-shadow-[4px_4px_0_rgba(58,44,39,1)]">
        How It Works
      </h1>
    </div>

    {/* Steps */}
    <section className="py-20 border-b-4 border-retro-brown bg-white">
      <div className="max-w-2xl mx-auto px-6 flex flex-col gap-10">
        {steps.map((s) => (
          <Step key={s.num} {...s} />
        ))}
      </div>
    </section>

    {/* Pricing */}
    <section className="py-20 border-b-4 border-retro-brown bg-retro-cream">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-14">
          <h2 className="font-display text-5xl md:text-6xl uppercase tracking-tighter mb-3">Pricing</h2>
          <p className="font-body font-bold text-gray-500 text-lg">Simple, transparent pricing. No hidden fees.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
          <PriceCard title="Standard Ad" price="299" features={standardFeatures} />
          <PriceCard title="Featured Ad" price="499" features={featuredFeatures} isPopular />
        </div>
      </div>
    </section>

    {/* Multi-mailing discounts */}
    <section className="py-20 border-b-4 border-retro-brown bg-white">
      <div className="max-w-2xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tighter mb-3">Save With Multiple Mailings</h2>
        </div>
        <div className="border-4 border-retro-brown rounded-[2rem] overflow-hidden shadow-retro">
          {discounts.map((row, i) => (
            <div
              key={row.label}
              className={`flex items-center justify-between px-8 py-5 font-body font-bold text-lg ${i !== discounts.length - 1 ? 'border-b-4 border-retro-brown' : ''} ${row.highlight ? 'bg-retro-yellow' : 'bg-white'}`}
            >
              <span>{row.label}</span>
              <span className={`font-display text-2xl uppercase ${row.highlight ? 'text-retro-brown' : 'text-retro-teal'}`}>{row.savings}</span>
            </div>
          ))}
        </div>
        <p className="font-body font-bold text-gray-500 text-center mt-8 leading-snug">
          Businesses that advertise consistently typically see stronger brand recognition and response rates than one-time advertisers.
        </p>
      </div>
    </section>

    {/* Category exclusivity */}
    <section className="py-20 bg-retro-blue">
      <div className="max-w-2xl mx-auto px-6 text-center">
        <div className="inline-block bg-retro-yellow border-4 border-retro-brown rounded-full px-6 py-2 font-display text-sm uppercase tracking-widest mb-8 shadow-retro-sm">
          Category Exclusivity
        </div>
        <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter text-retro-cream drop-shadow-[4px_4px_0_rgba(58,44,39,1)] mb-8">
          One Business Per Industry
        </h2>
        <p className="font-body text-xl font-bold bg-retro-cream text-retro-brown p-8 border-4 border-retro-brown rounded-[2rem] shadow-retro leading-snug">
          To maximize value for our advertisers, we limit each mailing to one business per category whenever possible.
        </p>
      </div>
    </section>

  </div>
);

export default HowItWorks;
